import type { GridSortModel } from '@mui/x-data-grid-pro';
import {
  FieldOption,
  SaleDefaultFieldOrder,
  SortDirection,
} from '@/components/publicSearch/constants';
import type {
  SaleFilteredSortOptionInput,
  SaleSortField,
  SortDirectionOrder,
} from '@/graphqlClient/types';
import { SALE_DATE_FILTER, SaleDateFilter } from '@/constants/saleFilters';

/**
 * Server-side sort for the sale-catalogue list.
 *
 * An explicit grid sort always wins. With none, ILRX-4693 asks the default to
 * mirror i4, which kept semen catalogues on a separate page and listed the
 * nearest upcoming sale first:
 *
 *   1. SALE_TYPE ascending — the raw two-letter codes sort SL < XL, so semen
 *      (XL/XR) lands below the live sales. Without this, semen catalogues
 *      carry far-future dates and floated to the top of a date sort.
 *   2. SALE_DATE — ascending so the closest sale is at the top. The "all sale
 *      dates" view also carries completed sales, where oldest-first would be
 *      useless, so that view stays newest-first.
 */
export function buildSaleSortOptions(
  gridSortModel: GridSortModel,
  saleDateFilter: SaleDateFilter,
): SaleFilteredSortOptionInput[] {
  const validSortFields = new Set(
    SaleDefaultFieldOrder.map(({ value }) => value),
  );

  const options = gridSortModel
    .filter(
      ({ field, sort }) =>
        field && sort && validSortFields.has(field as string),
    )
    .map(({ field, sort }) => ({
      direction: SortDirection[sort!] as SortDirectionOrder,
      field: field as SaleSortField,
    }));

  if (options.length > 0) return options;

  return [
    {
      field: FieldOption.saleType.value as SaleSortField,
      direction: SortDirection.asc as SortDirectionOrder,
    },
    {
      field: FieldOption.saleDate.value as SaleSortField,
      direction: (saleDateFilter === SALE_DATE_FILTER.ALL
        ? SortDirection.desc
        : SortDirection.asc) as SortDirectionOrder,
    },
  ];
}
